/**
 * Formatting Service - handles text formatting operations
 */

import { inject, Injectable } from '@angular/core'
import { DOM_ELEMENT_NAMES, TAG_EDITOR_STRINGS } from '../tag-editor.constants'
import { FormattingState } from '../tag-editor.model'
import { DebugLoggingService } from './debug-logging.service'

@Injectable({ providedIn: 'root' })
export class FormattingService {
  private readonly debugLoggingService = inject(DebugLoggingService)

  /**
   * Apply bold formatting using Selection API
   */
  applyBold(): boolean {
    return this.toggleFormatting('strong')
  }

  /**
   * Apply italic formatting using Selection API
   */
  applyItalic(): boolean {
    return this.toggleFormatting('em')
  }

  /**
   * Apply underline formatting using Selection API
   */
  applyUnderline(): boolean {
    return this.toggleFormatting('u')
  }

  /**
   * Toggle formatting wrapper (strong/em/u) around selection
   */
  private toggleFormatting(tagName: string): boolean {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    if (range.collapsed) {
      return false
    }

    try {
      // Check if selection is already wrapped in this formatting
      let commonAncestor: Node | null = range.commonAncestorContainer
      if (commonAncestor.nodeType === Node.TEXT_NODE) {
        commonAncestor = commonAncestor.parentNode
      }

      // If already wrapped, remove formatting
      let formatElement = commonAncestor as HTMLElement
      while (formatElement && formatElement.nodeName.toLowerCase() !== tagName) {
        formatElement = formatElement.parentElement as HTMLElement
      }

      if (formatElement && formatElement.nodeName.toLowerCase() === tagName) {
        // Unwrap: move contents out and remove wrapper
        const parent = formatElement.parentNode
        while (formatElement.firstChild) {
          parent?.insertBefore(formatElement.firstChild, formatElement)
        }
        parent?.removeChild(formatElement)
        return true
      }

      // Wrap selection with formatting element
      const formatWrapper = document.createElement(tagName)
      range.surroundContents(formatWrapper)
      selection.removeAllRanges()
      selection.addRange(range)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', `Failed to toggle ${tagName} formatting:`, error)
      return false
    }
  }

  /**
   * Insert ordered list using Selection API
   */
  insertOrderedList(): boolean {
    return this.insertList(DOM_ELEMENT_NAMES.ORDERED_LIST)
  }

  /**
   * Insert unordered list using Selection API
   */
  insertUnorderedList(): boolean {
    return this.insertList(DOM_ELEMENT_NAMES.UNORDERED_LIST)
  }

  /**
   * Insert list (ol/ul) wrapping selection or creating new list item
   */
  private insertList(listType: 'ol' | 'ul'): boolean {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    let node: Node | null = range.commonAncestorContainer

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode
    }

    // Check if cursor is already inside a list of the same type
    let currentList: HTMLElement | null = null
    let currentListItem: HTMLElement | null = null
    let currentNode: Node | null = node

    while (currentNode && currentNode.nodeType !== Node.DOCUMENT_NODE) {
      const element = currentNode as HTMLElement
      if (element.tagName?.toLowerCase() === DOM_ELEMENT_NAMES.LIST_ITEM) {
        currentListItem = element
      }
      if (element.tagName?.toLowerCase() === listType) {
        currentList = element
        break
      }
      currentNode = currentNode.parentNode
    }

    try {
      // If already inside list of same type, just add new list item
      if (currentList && currentList.tagName.toLowerCase() === listType) {
        const newListItem = document.createElement(DOM_ELEMENT_NAMES.LIST_ITEM)
        newListItem.appendChild(document.createTextNode(''))

        // Insert after current list item if exists, otherwise append to list
        if (currentListItem?.nextSibling) {
          currentList.insertBefore(newListItem, currentListItem.nextSibling)
        } else {
          currentList.appendChild(newListItem)
        }

        // Place cursor inside new list item
        const newRange = document.createRange()
        newRange.setStart(newListItem, 0)
        newRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(newRange)

        return true
      }

      // If inside different list type, convert it
      if (currentList && currentList.tagName.toLowerCase() !== listType) {
        // Convert existing list to new type
        const newList = document.createElement(listType)
        while (currentList.firstChild) {
          newList.appendChild(currentList.firstChild)
        }
        currentList.parentNode?.replaceChild(newList, currentList)

        // Place cursor at same position
        const newRange = document.createRange()
        if (currentListItem) {
          newRange.setStart(currentListItem, 0)
          newRange.collapse(true)
        } else {
          newRange.selectNodeContents(newList)
        }
        selection.removeAllRanges()
        selection.addRange(newRange)

        return true
      }

      if (range.collapsed) {
        // Insert new list item at cursor
        const list = document.createElement(listType)
        const listItem = document.createElement('li')
        listItem.appendChild(document.createTextNode(''))
        list.appendChild(listItem)
        range.insertNode(list)

        // Place cursor inside list item
        const newRange = document.createRange()
        newRange.setStart(listItem, 0)
        newRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(newRange)

        return true
      }

      // Wrap selection in list
      const list = document.createElement(listType)
      const listItem = document.createElement('li')
      listItem.appendChild(range.extractContents())
      list.appendChild(listItem)
      range.insertNode(list)

      // Select the list
      const newRange = document.createRange()
      newRange.selectNodeContents(list)
      selection.removeAllRanges()
      selection.addRange(newRange)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', `Failed to insert ${listType} list:`, error)
      return false
    }
  }

  /**
   * Create link using Selection API
   */
  createLink(url: string): boolean {
    if (!url) {
      return false
    }

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    if (range.collapsed) {
      return false
    }

    // Ensure URL has protocol
    const formattedUrl =
      url.startsWith('http://') || url.startsWith('https://') ? url : `${TAG_EDITOR_STRINGS.DEFAULT_URL}${url}`

    try {
      const linkElement = document.createElement(DOM_ELEMENT_NAMES.ANCHOR.toLowerCase()) as HTMLAnchorElement
      linkElement.href = formattedUrl
      linkElement.appendChild(range.extractContents())
      range.insertNode(linkElement)

      // Select the link
      const newRange = document.createRange()
      newRange.selectNodeContents(linkElement)
      selection.removeAllRanges()
      selection.addRange(newRange)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', 'Failed to create link:', error)
      return false
    }
  }

  /**
   * Remove all formatting using Selection API
   */
  removeFormat(): boolean {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    if (range.collapsed) {
      return false
    }

    try {
      // Extract text content and remove formatting elements
      const contents = range.extractContents()
      const textContent = contents.textContent || ''

      // Create text node with extracted content
      const textNode = document.createTextNode(textContent)
      range.insertNode(textNode)

      // Select inserted text
      const newRange = document.createRange()
      newRange.selectNodeContents(textNode)
      selection.removeAllRanges()
      selection.addRange(newRange)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', 'Failed to remove format:', error)
      return false
    }
  }

  /**
   * Get current formatting state by inspecting DOM
   */
  getFormattingState(): FormattingState {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return {
        isBold: false,
        isItalic: false,
        isUnderline: false,
        isOrderedList: false,
        isUnorderedList: false,
        hasLink: false,
      }
    }

    const range = selection.getRangeAt(0)
    let node: Node | null = range.commonAncestorContainer

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode
    }

    const formattingState = {
      isBold: false,
      isItalic: false,
      isUnderline: false,
      isOrderedList: false,
      isUnorderedList: false,
      hasLink: false,
    }

    // Traverse up the DOM tree to find formatting elements
    while (node && node.nodeType !== Node.DOCUMENT_NODE) {
      const element = node as HTMLElement
      const tagName = element.tagName?.toLowerCase()

      if (tagName === 'strong' || tagName === 'b') {
        formattingState.isBold = true
      } else if (tagName === 'em' || tagName === 'i') {
        formattingState.isItalic = true
      } else if (tagName === 'u') {
        formattingState.isUnderline = true
      } else if (tagName === DOM_ELEMENT_NAMES.ORDERED_LIST) {
        formattingState.isOrderedList = true
      } else if (tagName === DOM_ELEMENT_NAMES.UNORDERED_LIST) {
        formattingState.isUnorderedList = true
      } else if (tagName === DOM_ELEMENT_NAMES.ANCHOR.toLowerCase()) {
        formattingState.hasLink = true
      }

      node = node.parentNode
    }

    return formattingState as FormattingState
  }

  /**
   * Insert HTML at current cursor position using Selection API
   */
  insertHTML(html: string): boolean {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)

    try {
      // Parse HTML string into DOM nodes
      const tempDiv = document.createElement(DOM_ELEMENT_NAMES.DIV)
      tempDiv.innerHTML = html

      // Extract all nodes from the temporary container
      const fragment = document.createDocumentFragment()
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild)
      }

      // Delete any selected content first
      range.deleteContents()

      // Insert entire fragment at once to preserve correct order
      // (inserting nodes one by one reverses their order)
      range.insertNode(fragment)

      // Move cursor to end of inserted content
      // After insertion, range.startContainer points to the container where fragment was inserted
      range.collapse(false) // Collapse to end
      selection.removeAllRanges()
      selection.addRange(range)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', 'Failed to insert HTML:', error)
      return false
    }
  }

  /**
   * Insert text at current cursor position using Selection API
   */
  insertText(text: string): boolean {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)

    try {
      // Delete any selected content first
      range.deleteContents()

      // Create text node and insert it
      const textNode = document.createTextNode(text)
      range.insertNode(textNode)

      // Move cursor after the inserted text
      range.setStartAfter(textNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', 'Failed to insert text:', error)
      return false
    }
  }

  /**
   * Insert line break at current cursor position using Selection API
   */
  insertLineBreak(): boolean {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)

    // Create <br> element
    const br = document.createElement(DOM_ELEMENT_NAMES.BR)

    try {
      // Delete any selected content first
      range.deleteContents()

      // Insert <br> at cursor position
      range.insertNode(br)

      // Move cursor after the <br>
      range.setStartAfter(br)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)

      return true
    } catch (error) {
      this.debugLoggingService.logWithService('FormattingService', 'Failed to insert line break:', error)
      return false
    }
  }
}
